import type { SelectHTMLAttributes } from "react";

import { useTranslation } from "@/i18n/use-translation";
import { withCurrentValue, type ConfigOption } from "@/lib/config-options";

/** A `<select>` over one of the shared config lists (POS2-1140).
 *
 * A plain native select rather than the Radix one used by `ConfigOverrides`, because this
 * has to work with `register`: react-hook-form fills registered inputs through their refs
 * when `reset()` lands the API's values, and Radix's Select keeps its options in the DOM
 * only while open, so a value arriving after mount left the trigger blank. A native select
 * always holds its options, so the label is right on first paint and the field needs no
 * `Controller`, no `watch`, and no re-render plumbing.
 *
 * `current` is the value as stored, which is offered even when the list does not carry it
 * — otherwise a row written before these lists existed becomes unsubmittable, and for a
 * timezone that silently moves the org's reporting day.
 */
export function ConfigSelect({
  id,
  options,
  current,
  ...selectProps
}: {
  id: string;
  options: readonly ConfigOption[];
  current?: string | null;
} & SelectHTMLAttributes<HTMLSelectElement>) {
  const { t } = useTranslation();
  return (
    <select
      id={id}
      className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive"
      {...selectProps}
    >
      {withCurrentValue(t, options, current).map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
